import React from "react";
import {Button, Card, Divider, Input, List, Typography} from "antd";
import {LinesContext, UserContext, WindowContext, WorkflowsContext} from '@web/hooks/context';
import {notifyAndTrack} from "@web/components/notification";
import InputSaveButton from "@web/components/InputSaveButton";
import Wrapper from './components/Wrapper';
import JobsList from './JobsList';
import JobCard from '@web/components/JobCard';
import {AddJobButton} from "./components/AddJobButton";
import {addJob} from '@web/lib/workflow.jobs';
import {getCollection, getDocRef} from '@web/lib/firestore.db';
import ItemApi from '@web/lib/ItemApi';

function WorkflowMain() {
  const wtx = React.useContext(WorkflowsContext);
  const ctx = React.useContext(LinesContext);
  const [, h] = React.useContext(UserContext);
  const [isMobile, width, height] = React.useContext(WindowContext);
  const [agentId] = React.useState(wtx.worklfow?.agentId || "ai-workflow-assistant");
  const [name, setName] = React.useState("");
  const [appDescription, setAppDescription] = React.useState("");
  const [edit, setEdit] = React.useState(!wtx.worklfow?.name || !wtx.worklfow?.description || !wtx.worklfow?.access);
  const [service, setService] = React.useState(null);
  const api = new ItemApi(`${h}/${wtx.workflowId}`);

  const createWorkflow = async () => {
    return wtx.workflow ?
      Promise.resolve() :
      api.set({
        name: "My Workflow",
        agentId,
      });
  }

  const patch = async (body) => {
    await createWorkflow();
    await api.update(
      {...body},
      () => notifyAndTrack.success("updated"),
      (error) => notifyAndTrack.error("Error updating workflow", error.message),
    );
  }

  const onClickSave = () => {
    setEdit(false);
  }

  /**
   * @summary Add job
   * @param {models.WorkflowJob.Data & {events:Record<models.WorkflowJob.WorkflowJobEvent, string>}} subscription
   * @param {models.WorkflowJob.WorkflowJobEvent} event
   */
  const onAddWorkflowJob = (subscription, event) => {
    const workflowRef = getDocRef(wtx.workflowId);
    return addJob(
      ctx.basePath,
      subscription.type,
      workflowRef,
      wtx.workflow,
      event,
      subscription.collectionPath &&
      getCollection(subscription.collectionPath),
    )
    .catch((error) => {
      notifyAndTrack.error("Error creating job", error.message);
    });
  }

  const nameOnChange = (e) => setName(e.target.value);

  const appDescriptionOnChange = (e) => setAppDescription(e.target.value);

  return <Wrapper isMobile={isMobile} height={height}>
    <div style={{width: "100%", maxWidth: 900, margin: "0 auto", overflow: "auto"}}>
      <div className="space space-col relative" style={{maxWidth: 900}}>
        <div style={{width: "100%", maxWidth: 900, overflow: "hidden", height: 50}}>
          <Typography.Title
            className="absolute"
            level={2}
            style={{margin: 0}}
          >
            {wtx.workflow?.name ?? wtx.workflow?.name ?? "My Workflow"}
          </Typography.Title>
        </div>
        <div className="font-xl muted">{wtx.workflow?.description}</div>
        <br/>
        <Card
          className="card-simple card-transparent card-rounded card-no-header card-no-separator"
          bordered={false}
        >
          <div className="space space-col">
            {edit ?
              <List
                size="large"
                footer={
                  <div className="space space-col">
                    <Divider />
                    <div className="space space-center-y space-between">
                      {edit &&
                        <Button className="green-color" shape="round" size="large" onClick={onClickSave}>Save</Button>}
                    </div>
                  </div>
                }>
                <List.Item
                  actions={[
                    <div className="space space-center-y relative" style={{width: 440}}>
                      <Input
                        maxLength={30}
                        value={name}
                        placeholder={name || wtx.worklfow?.name || "Name your app"}
                        size="large"
                        bordered={false}
                        className={`input-bold`}
                        onChange={nameOnChange}
                      />
                      <InputSaveButton
                        onClick={() => patch({name, agentId})}
                        show={!!name}
                        disabled={name?.length < 3}
                      />
                    </div>,
                  ]}
                >
                  <List.Item.Meta
                    title={<span className="font-xl bolded">Name</span>}
                    description={service?.type === "data" && <span className="font-lg ai-text">{service?.description}</span>}
                  />
                </List.Item>
                <List.Item
                  actions={[
                    <div className="space space-center-y relative" style={{width: 440}}>
                      <Input
                        maxLength={200}
                        value={appDescription}
                        placeholder={appDescription || "What does your app do?"}
                        size="large"
                        bordered={false}
                        className={`input-bold`}
                        onChange={appDescriptionOnChange}
                      />
                      <InputSaveButton
                        onClick={() => patch({description: appDescription})}
                        show={!!appDescription}
                        disabled={appDescription?.length < 3}
                      />
                    </div>,
                  ]}
                >
                  <List.Item.Meta
                    title={<span className="font-xl bolded">Description</span>}
                    description={service?.type === "data" && <span className="font-lg ai-text">{service?.description}</span>}
                  />
                </List.Item>
                <List.Item
                  actions={[
                    <Button
                      key="public"
                      size="large"
                      className="icon-btn"
                      ghost
                      disabled={wtx.worklfow?.access === "public"}
                      onClick={() => patch({access: "public"})}
                    >
                      Public
                    </Button>,
                    <Button
                      key="private"
                      size="large"
                      className="icon-btn"
                      ghost
                      disabled={wtx.worklfow?.access === "private"}
                      onClick={() => patch({access: "private"})}
                    >
                      Private
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    title={<span className="font-xl bolded">Access</span>}
                  />
                </List.Item>
              </List> :
              <Card className="card-simple card-rounded card-h-100" extra={<Button onClick={() => setEdit(true)}>Edit</Button>}>
                <div className="space space-col">
                  <JobsList
                    jobPaths={wtx.worklfow?.jobPaths}
                    component={JobCard}
                    locale={{
                      emptyText: "No jobs found",
                    }}
                  />
                  <AddJobButton onAdd={onAddWorkflowJob}  />
                </div>
              </Card>}
          </div>
        </Card>
        <div style={{width: "100%", maxWidth: 900, paddingTop: "10px", height: 100}} className="relative">
        </div>
      </div>
    </div>
  </Wrapper>
}

export default WorkflowMain;
